// Pure retry/backoff bookkeeping for the auth bootstrap fetch (ENG-1745).
//
// Kept free of React and `import.meta` so it is directly unit-testable — same
// pattern as utils/request-timeouts.ts. use-authorize.hook.tsx holds the
// actual module-level state (consistent with its other cross-instance dedup
// flags); this module only computes what that state should become.

// Delay before each successive auto-retry, indexed by (failureCount - 1).
// The last value repeats for any failureCount beyond the array's length.
export const BOOTSTRAP_RETRY_BACKOFF_MS = [2000, 5000, 15000, 30000];

// Once a token has failed this many times in a row, stop auto-retrying in the
// background — surface the error and wait for a manual retry instead of
// backing off forever with no user-visible feedback.
export const MAX_AUTO_RETRY_ATTEMPTS = BOOTSTRAP_RETRY_BACKOFF_MS.length;

export type BootstrapFailureRecord = {
  token: string;
  failureCount: number;
  // Epoch ms of the next allowed auto-retry. `Infinity` once
  // MAX_AUTO_RETRY_ATTEMPTS is exceeded — only a manual retry unblocks it.
  nextRetryAt: number;
};

export const getBackoffDelayMs = (failureCount: number): number =>
  BOOTSTRAP_RETRY_BACKOFF_MS[
    Math.min(failureCount, BOOTSTRAP_RETRY_BACKOFF_MS.length) - 1
  ];

// Builds the record to store after a fresh failure for `token`, given the
// previous record (or undefined/a different token's record on this token's
// first failure in the current record).
export const recordBootstrapFailure = (
  token: string,
  previous: BootstrapFailureRecord | null | undefined,
  now: number
): BootstrapFailureRecord => {
  const failureCount =
    (previous?.token === token ? previous.failureCount : 0) + 1;
  const nextRetryAt =
    failureCount <= MAX_AUTO_RETRY_ATTEMPTS
      ? now + getBackoffDelayMs(failureCount)
      : Infinity;
  return { token, failureCount, nextRetryAt };
};

// Whether `token` is currently blocked from a new bootstrap attempt by a
// prior failure's backoff window (or the manual-retry-only ceiling).
export const isBootstrapBlocked = (
  record: BootstrapFailureRecord | null | undefined,
  token: string,
  now: number
): boolean => record?.token === token && now < record.nextRetryAt;
